import React, {useCallback, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {MenuItem, Select} from '@mui/material';
import {useGetReportQuery} from "../../services/exports";
import RangePicker from "../../components/ui/RangePicker";
import {getFormattedTodayDate} from "../../utils/intlDate";
import * as Styles from "../../layouts/PrivateLayout.styles";
import Button from "../../components/ui/Button";
import {useGetClassesQuery} from "../../services/classes";

const reportTypes = [
    'enrollments_report',
    'enrollments_by_class_report',
    'locations_report',
    'customers_report',
    'classes_report',
    'orders_report',
];

const CSVExportPage: React.FC = () => {
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState(getFormattedTodayDate().formatString);
    const [endDate, setEndDate] = useState(getFormattedTodayDate().formatString);
    const [reportType, setReportType] = useState(reportTypes[0]);
    const [isExporting, setIsExporting] = useState(false);
    const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
    const [timestamp, setTimestamp] = useState(Date.now());
    const downloadedRef = useRef(false);

    const { data: classesData } = useGetClassesQuery({});

    const { data: reportData, isFetching } = useGetReportQuery(
        {
            reportType,
            dateRange: {
                start: startDate,
                end: endDate,
            },
            classId: selectedClassId,
            timestamp,
        },
        {
            skip: !isExporting || (reportType === 'enrollments_by_class_report' && !selectedClassId),
        }
    );

    const downloadReport = useCallback((data: Blob) => {
        const url = window.URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${reportType}.csv`);
        document.body.appendChild(link);
        link.click();

        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
        setIsExporting(false);
    }, [reportType]);

    const handleExport = useCallback(() => {
        setIsExporting(true);
        setTimestamp(Date.now());
        downloadedRef.current = false;
    }, []);

    if (reportData && isExporting && !downloadedRef.current) {
        downloadReport(reportData);
        setIsExporting(false);
        downloadedRef.current = true;
    }

    return (
        <div>
            <Styles.PageTitleContainer>
                <h1>Export Reports</h1>
                <Styles.PageTitleContainerButtons>
                    <RangePicker
                        startDate={startDate}
                        endDate={endDate}
                        setStartDate={setStartDate}
                        setEndDate={setEndDate}
                    />
                </Styles.PageTitleContainerButtons>
            </Styles.PageTitleContainer>
            <div style={{display: 'flex', flexDirection: 'column', gap: 20}}>
                <div>Report Type: </div>
                <Select
                    value={reportType}
                    onChange={(e) => {
                        setReportType(e.target.value as string);
                        setSelectedClassId(null);
                    }}
                >
                    {reportTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                            {type.replace(/_/g, ' ').toUpperCase()}
                        </MenuItem>
                    ))}
                </Select>

                {reportType === 'enrollments_by_class_report' && (
                    <>
                        <div>Select Class: </div>
                        <Select
                            value={selectedClassId || ''}
                            onChange={(e) => setSelectedClassId(e.target.value as string)}
                        >
                            {classesData?.classes?.map((classItem) => (
                                <MenuItem key={classItem.id} value={classItem.id}>
                                    {classItem.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </>
                )}

                <Styles.PageTitleContainerButtons>
                    <Button onClick={() => navigate(-1)}>Cancel</Button>
                    <Button
                        onClick={handleExport}
                        cc-color={'purple'}
                        disabled={isFetching || isExporting || (reportType === 'enrollments_by_class_report' && !selectedClassId)}
                    >
                        {isFetching || isExporting ? 'Exporting...' : 'Export'}
                    </Button>
                </Styles.PageTitleContainerButtons>
            </div>
        </div>
    );
};

export default CSVExportPage;
